import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import { useDeleteFlow } from '../../../components/confirm';
import { PRE_LOGIN_FORM } from './PreLoginSettingsDetailPage';
import { srTestKey } from './preLoginSr';
import './preLoginSettings.css';
import type { PreLoginTest, PreLoginTestInput, PreLoginBottle } from './types';

interface Props {
  /** The sample whose tests are being assigned; null = none picked yet. */
  sampleOid: string | null;
  /** Its display id, for the section heading. */
  sampleId: string | null;
  /** The sample's tests as loaded — one row per PARAMETER. */
  tests: PreLoginTest[];
  /** The sample's bottle rows — the BottleID column reads them. */
  bottles: PreLoginBottle[];
  /** Re-reads the parent's tests / containers / parameters after a save. */
  onSaved: () => void;
  /** Edit → Registration's Test Assignment window for this sample. */
  onEdit: () => void;
  /** BottleID cell → Registration's Containers window focused on that test. */
  onOpenContainers: (testKey: string) => void;
  /** Parameter cell → Registration's parameter checklist for that test. */
  onOpenParams: (testKey: string) => void;
  /** Module Access Control: a role without Write sees the assigned tests but gets no
   *  Edit / Delete bar, no SubOut cell edit and no Containers / Parameter editors
   *  (those cells show their value as plain text). Unset = unchanged. */
  readOnly?: boolean;
}

/** A test as the ASSIGNMENT grid shows it — one row per (test, component),
 *  carrying the parameter rows that make it up. */
interface AssignedTest {
  key:           string;
  test:          string | null;
  method:        string | null;
  component:     string | null;
  matrix:        string | null;
  /** The group a member belongs to. */
  testGroupName: string | null;
  /** Letters of the bottles allocated to this test (computed). */
  bottleId:      string;
  /** "All Param" / "Customized" — Registration's wording. */
  parameter:     string;
  subOut:        boolean;
  rows:          PreLoginTest[];
}

/** Groups parameter rows into the tests a user actually assigns. */
function groupTests(rows: PreLoginTest[], bottles: PreLoginBottle[]): AssignedTest[] {
  const letters = new Map<string, string[]>();
  for (const b of bottles) {
    const l = (b.bottleIdInternal ?? '').trim();
    if (!l || !b.testMethodOid) continue;
    const k = b.testMethodOid.toLowerCase();
    const list = letters.get(k) ?? [];
    if (!list.includes(l)) list.push(l);
    letters.set(k, list);
  }

  const by = new Map<string, AssignedTest>();
  for (const r of rows) {
    const key = srTestKey(r.testMethodOid ?? r.test, r.components);
    const hit = by.get(key);
    if (hit) {
      hit.rows.push(r);
      if (r.testParams === 'Customized') hit.parameter = 'Customized';
    } else {
      by.set(key, {
        key, test: r.test, method: r.method, component: r.components || 'Default', matrix: r.matrix,
        testGroupName: r.testGroupName,
        bottleId: (letters.get((r.testMethodOid ?? '').toLowerCase()) ?? []).sort().join(','),
        parameter: r.testParams === 'Customized' ? 'Customized' : 'All Param',
        subOut: r.subOut, rows: [r],
      });
    }
  }
  return [...by.values()];
}

/**
 * **Test Assignment** — the second grid beneath the Samples grid, assigning
 * tests to the selected sample. It follows Sample Registration's own
 * (`SampleLoginView` → `TestAssignment`): an Edit / Delete bar, a BottleID combo
 * that opens the Containers window, and a Parameter button that opens the analyte
 * checklist. Edit opens Registration's three-grid Test Assignment window — the
 * windows themselves are Registration's components, driven by `usePreLoginSr`.
 *
 * <p>A user assigns a TEST, but `dbo.COCSettingsTest` is keyed on `Testparameter`
 * — one row per parameter — so this grid groups the stored rows into tests and
 * Delete takes all of a test's rows together.</p>
 */
export default function PreLoginTestAssignment({
  sampleOid, sampleId, tests, bottles, onSaved, onEdit, onOpenContainers, onOpenParams, readOnly = false,
}: Props) {
  const notify = useNotify();
  const runDelete = useDeleteFlow();

  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSel(new Set()); }, [tests]);

  const grouped = useMemo(() => groupTests(tests, bottles), [tests, bottles]);

  const columns: AppColumn<AssignedTest>[] = useMemo(() => [
    { field: 'matrix',        header: 'Matrix',        width: 110 },
    { field: 'test',          header: 'Test',          width: 220 },
    { field: 'testGroupName', header: 'TestGroupName', width: 150 },
    // BottleID, right after TestGroupName as in Registration: the same combo-look
    // control, opening the Containers window on that test.
    { field: 'bottleId', header: 'BottleID', width: 90, sortable: false,
      render: (g: AssignedTest) => readOnly ? <span>{g.bottleId || '—'}</span> : (
        <button type="button" className="sr-ba-combo" aria-label={`Bottle ID for ${g.test ?? ''}`}
          onClick={e => { e.stopPropagation(); onOpenContainers(g.key); }}>
          <span>{g.bottleId || '—'}</span><i className="ti ti-chevron-down" />
        </button>
      ),
      text: (g: AssignedTest) => g.bottleId },
    { field: 'method',        header: 'Method',        width: 140 },
    { field: 'component',     header: 'Components',    width: 130 },
    // SubOut is the one editable cell (legacy batch edit); it saves at once.
    { field: 'subOut',        header: 'SubOut',        width: 70,
      ...(readOnly ? {} : { edit: { type: 'combo' as const, options: ['true', 'false'], clearable: false } }) },
    // Parameter: Registration's "All Param ▾" button → the analyte checklist.
    { field: 'parameter',     header: 'Parameter',     width: 120, sortable: false,
      render: (g: AssignedTest) => readOnly ? <span>{g.parameter}</span> : (
        <button className="sr-edit-cell sr-edit-cell--text"
          style={{ color: 'var(--ai-pill-info-fg)', fontWeight: 600 }}
          onClick={e => { e.stopPropagation(); onOpenParams(g.key); }}>
          {g.parameter} ▾
        </button>
      ),
      text: (g: AssignedTest) => g.parameter },
  ], [onOpenContainers, onOpenParams, readOnly]);

  const toInputs = (list: PreLoginTest[]): PreLoginTestInput[] =>
    list.map(r => ({
      oid:              r.oid.startsWith('new:') ? null : r.oid,
      testParameterOid: r.testParameterOid ?? '',
      groupTestOid:     r.groupTestOid,
      isGroup:          r.isGroup,
      subOut:           r.subOut,
      subLabOid:        r.subLabOid,
      suboutQCTypeOid:  r.suboutQCTypeOid,
      preservativeOid:  r.preservativeOid,
      containerTypeOid: r.containerTypeOid,
      containerCount:   r.containerCount,
      dupBottle:        r.dupBottle,
      unitOid:          null,
      mdl:              null,
      rptLimit:         null,
      df:               null,
    }));

  /** Legacy `checkIfGroupTestCustomized`: once only SOME members of a group
   *  remain, the group is no longer a group - the survivors are flattened to
   *  ordinary tests (IsGroup false, GroupTest cleared). */
  const flattenBroken = (before: PreLoginTest[], after: PreLoginTest[]): PreLoginTest[] => {
    const broken = new Set<string>();
    for (const r of before) {
      if (r.isGroup && r.testGroupName && !after.some(a => a.oid === r.oid)) broken.add(r.testGroupName);
    }
    if (broken.size === 0) return after;
    return after.map(r => (r.isGroup && r.testGroupName && broken.has(r.testGroupName)
      ? { ...r, isGroup: false, groupTestOid: null, testGroupName: null } : r));
  };

  /** PUTs the whole resulting set, then has the parent re-read. */
  const persist = async (next: PreLoginTest[]) => {
    if (!sampleOid) return;
    await api.put(`/api/v1/pre-login-settings/samples/${sampleOid}/tests`, toInputs(next));
    onSaved();
  };

  /** SubOut toggled on a test: applied to every parameter row behind it and saved. */
  const onCellCommit = (row: AssignedTest, col: AppColumn<AssignedTest>, value: string) => {
    if (col.field !== 'subOut') return;
    const on = value === 'true';
    if (on === row.subOut) return;
    const ids = new Set(row.rows.map(r => r.oid));
    setBusy(true);
    persist(tests.map(r => (ids.has(r.oid) ? { ...r, subOut: on } : r)))
      .catch(e => {
        const m = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(m ?? 'Could not update SubOut.');
      })
      .finally(() => setBusy(false));
  };

  /** Removes the selected TESTS — every parameter row behind each one. */
  const removeSelected = () => {
    if (sel.size === 0) return;
    const doomed = new Set(
      grouped.filter(g => sel.has(g.key)).flatMap(g => g.rows.map(r => r.oid)));
    const count = sel.size;
    void runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Remove ${count} test${count === 1 ? '' : 's'} from this sample?`,
      confirmLabel: 'Remove',
      successMessage: 'Removed successfully.',
      delete: async () => {
        await persist(flattenBroken(tests, tests.filter(r => !doomed.has(r.oid))));
      },
    });
  };

  // The root carries NO inline margin or width: `.sr-sec` already sets
  // `margin: 0 0 8px`, and this card must dock exactly like the samples card above.
  return (
    <section className="sr-sec">
      <h3 className="sr-sec-h">
        Test Assignment{sampleId ? ' — ' : ''}
        <span style={{ color: 'var(--ai-orange-hover, #e8670a)' }}>{sampleId ?? ''}</span>
        <span className="sr-sec-note">
          {sampleOid
            ? (readOnly
              ? 'mark a Samples row above to switch sample'
              : 'mark a Samples row above to switch sample · Edit opens the Test Assignment window · Parameter opens the analyte checklist')
            : 'select a sample above to assign its tests'}
        </span>
      </h3>

      <div className="sr-sec-b">
        {/* Registration's Test Assignment bar: Edit · Delete. */}
        {!readOnly && (
        <div className="sr-subtoolbar" style={{ paddingTop: 0, marginBottom: 8 }}>
          <span style={{ display: 'flex', gap: 6 }}>
            <button className="ca-btn" disabled={!sampleOid || busy} onClick={onEdit}>
              <i className="ti ti-edit" /> Edit
            </button>
            <button className="ca-btn" disabled={sel.size === 0 || busy} onClick={removeSelected}>
              <i className="ti ti-trash" style={{ color: 'var(--ai-icon-danger, #c0392b)' }} /> Delete
            </button>
          </span>
        </div>
        )}

        <AppDataGrid<AssignedTest>
          rows={grouped}
          columns={columns}
          getRowId={r => r.key}
          selectable={!readOnly}
          selectedIds={sel}
          onSelectionChange={setSel}
          onCellCommit={readOnly ? undefined : onCellCommit}
          initialSort={{ key: 'test', dir: 'asc' }}
          autoFocusFirstRow={false}
          // This grid sits directly beneath the Samples grid, which scrolls
          // horizontally and so paints across the reserved scrollbar gutter.
          flushRight
          defaultPageSize={10}
          exportFilename="Pre-Login Test Assignment"
          emptyText={sampleOid
            ? (readOnly ? 'No tests assigned to this sample.' : 'No tests assigned to this sample — click Edit.')
            : 'Select a sample above.'}
        />
      </div>
    </section>
  );
}
